import { useNow } from '@/hooks/useNow'
import { useStatus } from '@/hooks/useStatus'
import { relativeTime, serviceLabel } from '@/lib/format'
import type { ServiceHealth } from '@/lib/types'
import { Card, CardBody, CardHeader } from './Card'
import { EmptyState } from './EmptyState'
import { Skeleton } from './Skeleton'

type Lamp = 'ok' | 'down' | 'unknown'

/*
 * Three lamp states, not two. A service that has never reported is not the same
 * as a service that answered and failed, and showing both in red trains people
 * to ignore the red.
 */
const LAMPS: Record<Lamp, string> = {
  ok: 'bg-ok',
  down: 'bg-danger',
  unknown: 'bg-faint',
}

function lampFor(service: ServiceHealth): Lamp {
  if (service.ok) return 'ok'
  if (!service.checkedAt || /not[ _]configured/i.test(service.message ?? '')) return 'unknown'
  return 'down'
}

/** Latency is the reading; "Offline"/"No data" stand in when there is none. */
function readingFor(service: ServiceHealth, lamp: Lamp): string {
  if (lamp === 'unknown') return 'No data'
  if (!service.ok) return 'Offline'
  return service.latencyMs !== null ? `${Math.round(service.latencyMs)} ms` : 'Online'
}

/** The status board in miniature: one lamp, one name, one reading per row. */
export function ServiceStatusList() {
  const { services, isLoading, isDegraded, checkedAt } = useStatus()
  const now = useNow(30_000)

  return (
    <Card>
      <CardHeader
        title="Server status"
        description={
          checkedAt ? `Checked ${relativeTime(checkedAt, now)}` : 'Health checks run in the background'
        }
      />
      <CardBody flush>
        {isLoading ? (
          <div className="space-y-2 px-4 py-4 sm:px-5">
            {Array.from({ length: 4 }, (_, index) => (
              <Skeleton key={index} className="h-8 w-full" />
            ))}
          </div>
        ) : services.length === 0 ? (
          <div className="px-4 py-4 sm:px-5">
            <EmptyState
              title="No health data yet"
              description="The background check has not reported in. This is normal right after a deploy."
            />
          </div>
        ) : (
          <ul>
            {services.map((service) => {
              const lamp = lampFor(service)
              return (
                <li
                  key={service.service}
                  className="board-row flex items-center gap-3 px-4 py-3 sm:px-5"
                >
                  <span aria-hidden="true" className={`size-2 shrink-0 rounded-full ${LAMPS[lamp]}`} />
                  <span className="signage min-w-0 flex-1 truncate text-[10px] text-ink">
                    {serviceLabel(service.service)}
                  </span>
                  <span
                    className={`shrink-0 font-mono text-[11px] tabular-nums ${
                      lamp === 'down' ? 'text-danger' : 'text-muted'
                    }`}
                  >
                    {readingFor(service, lamp)}
                  </span>
                  <span className="sr-only">
                    {service.ok ? 'Online' : 'Offline'}
                    {service.checkedAt ? `, checked ${relativeTime(service.checkedAt, now)}` : ''}
                  </span>
                </li>
              )
            })}
          </ul>
        )}

        {isDegraded ? (
          <p className="border-t border-line px-4 py-3 text-xs text-muted sm:px-5">
            Hermes keeps serving your account and last-known request data while the server is down.
          </p>
        ) : null}
      </CardBody>
    </Card>
  )
}
